/**
 * Typed bindings for /api/proxies.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type ProxyType = "socks5" | "http" | "https" | "mtproxy";

export type ProxyPhysicalType = "datacenter" | "residential" | "mobile" | "unknown";

export const PROXY_PHYSICAL_TYPES: ProxyPhysicalType[] = [
  "residential",
  "mobile",
  "datacenter",
  "unknown",
];

export interface ProxySummary {
  id: string;
  label: string;
  type: ProxyType;
  host: string;
  port: number;
  has_auth: boolean;
  country_code: string | null;
  country_name: string | null;
  city: string | null;
  latency_ms: number | null;
  is_alive: boolean | null;
  last_check_at: string | null;
  last_check_error: string | null;
  assigned_accounts_count: number;
  notes: string | null;
  physical_type: string | null;
  created_at: string;
}

export interface ParsedProxyLine {
  /** Only when the line carried a scheme we support (socks5://, http://, https://). */
  type?: ProxyType;
  host: string;
  port: number;
  username?: string;
  password?: string;
}

const SCHEME_TYPES: Record<string, ProxyType> = {
  socks5: "socks5",
  socks5h: "socks5",
  socks: "socks5",
  http: "http",
  https: "https",
};

/**
 * One proxy line → its parts, for the "paste it whole" field of the add form.
 * Same shapes the bulk import takes (`_parse_proxy_line` on the backend):
 *   login:password@host:port · host:port:login:password · host:port
 * each optionally prefixed with a scheme. Null when the line doesn't fit.
 */
export function parseProxyLine(raw: string): ParsedProxyLine | null {
  let s = raw.trim();
  let type: ProxyType | undefined;
  // Like the backend: whatever precedes the first "://" is the scheme, even
  // one we don't know ("socks5+tls://") — it must not leak into the login.
  const sep = s.indexOf("://");
  if (sep !== -1) {
    type = SCHEME_TYPES[s.slice(0, sep).toLowerCase()];
    s = s.slice(sep + 3);
  }
  let host: string;
  let portStr: string;
  let username: string | undefined;
  let password: string | undefined;
  const at = s.lastIndexOf("@");
  if (at !== -1) {
    const creds = s.slice(0, at);
    const colon = creds.indexOf(":");
    username = colon === -1 ? creds : creds.slice(0, colon);
    password = colon === -1 ? undefined : creds.slice(colon + 1);
    const parts = s.slice(at + 1).split(":");
    if (parts.length < 2) return null;
    [host, portStr] = parts;
  } else {
    const parts = s.split(":");
    if (parts.length === 2) [host, portStr] = parts;
    else if (parts.length === 4) [host, portStr, username, password] = parts;
    else return null;
  }
  host = host.trim();
  const port = Number(portStr.trim());
  if (!host || !/^\d+$/.test(portStr.trim()) || port < 1 || port > 65535) return null;
  return {
    type,
    host,
    port,
    username: username?.trim() || undefined,
    password: password?.trim() || undefined,
  };
}

export const PROXIES_KEY = ["proxies"] as const;

export function useProxies() {
  return useQuery<ProxySummary[]>({
    queryKey: PROXIES_KEY,
    queryFn: () => api.get<ProxySummary[]>("/api/proxies"),
  });
}

export function useCreateProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      label: string;
      type: ProxyType;
      host: string;
      port: number;
      username?: string;
      password?: string;
      secret?: string;
      notes?: string;
      physical_type?: string;
    }) => api.post<ProxySummary>("/api/proxies", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROXIES_KEY }),
  });
}

export function useUpdateProxy() {
  const qc = useQueryClient();
  return useMutation({
    // PATCH only accepts label / notes / physical_type — connection params
    // (type/host/port/auth) are immutable by design: to change those, delete
    // and recreate the proxy.
    mutationFn: (vars: {
      id: string;
      label?: string;
      notes?: string;
      physical_type?: string;
    }) => {
      const { id, ...payload } = vars;
      return api.patch<ProxySummary>(`/api/proxies/${id}`, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PROXIES_KEY }),
  });
}

export interface ProxyBulkError {
  line_number: number;
  raw: string;
  reason: string;
}

export interface ProxyBulkResult {
  created: ProxySummary[];
  errors: ProxyBulkError[];
}

export function useBulkCreateProxies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      type: ProxyType;
      physical_type?: string;
      label_prefix?: string;
      rotation_url?: string;
      text: string;
    }) => api.post<ProxyBulkResult>("/api/proxies/bulk", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROXIES_KEY }),
  });
}

export function useTestProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<ProxySummary>(`/api/proxies/${id}/test`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROXIES_KEY }),
  });
}

export function useTestAllProxies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ProxySummary[]>("/api/proxies/test_all"),
    onSuccess: (rows) => qc.setQueryData(PROXIES_KEY, rows),
  });
}

export interface ProxyProbeResult {
  is_alive: boolean;
  error: string | null;
  country_code: string | null;
  country_name: string | null;
  city: string | null;
  latency_ms: number | null;
}

export function useProbeProxy() {
  return useMutation({
    mutationFn: (payload: {
      type: ProxyType;
      host: string;
      port: number;
      username?: string;
      password?: string;
      secret?: string;
    }) => api.post<ProxyProbeResult>("/api/proxies/probe", payload),
  });
}

export function useDeleteProxy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reassign_to?: string }) => {
      const qs = vars.reassign_to
        ? `?reassign_to=${encodeURIComponent(vars.reassign_to)}`
        : "";
      return api.delete<void>(`/api/proxies/${vars.id}${qs}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROXIES_KEY });
      // Account proxy_id values may have moved — refresh them too.
      qc.invalidateQueries({ queryKey: ["accounts"] });
    },
  });
}
